import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

const SITE_ORIGIN = 'https://decobank.nl';
const DEFAULT_TITLE = 'De CoBank | Oefenvragen en Klinische Casussen voor Coschappen';
const DEFAULT_DESCRIPTION = (
  'Oefen voor je coschappen met klinische vragen en casussen per specialisme. '
  + 'Het platform voor coassistenten met directe feedback en duidelijke uitleg.'
);

const ROUTE_META = [
  {
    matcher: { path: '/', end: true },
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    robots: 'index,follow',
    canonicalPath: '/',
    ogType: 'website'
  },
  {
    matcher: { path: '/login', end: true },
    title: 'Inloggen | De CoBank',
    description: 'Log in op De CoBank en ga verder met oefenen tijdens je coschappen.',
    robots: 'index,follow',
    canonicalPath: '/login',
    ogType: 'website'
  },
  {
    matcher: { path: '/register', end: true },
    title: 'Registreren | De CoBank',
    description: 'Maak een account aan op De CoBank en start met oefenen voor je coschappen.',
    robots: 'index,follow',
    canonicalPath: '/register',
    ogType: 'website'
  },
  {
    matcher: { path: '/support', end: true },
    title: 'Support | De CoBank',
    description: 'Hulp nodig met De CoBank? Neem contact op met support of bekijk de supportpagina.',
    robots: 'index,follow',
    canonicalPath: '/support',
    ogType: 'website'
  },
  {
    matcher: { path: '/forgot-password', end: true },
    title: 'Wachtwoord Vergeten | De CoBank',
    description: 'Vraag een resetlink aan om je wachtwoord van De CoBank opnieuw in te stellen.',
    robots: 'noindex,follow',
    canonicalPath: '/forgot-password',
    ogType: 'website'
  },
  {
    matcher: { path: '/reset-password', end: true },
    title: 'Wachtwoord Resetten | De CoBank',
    description: 'Stel je wachtwoord opnieuw in voor je De CoBank account.',
    robots: 'noindex,follow',
    canonicalPath: '/reset-password',
    ogType: 'website'
  },
  {
    matcher: { path: '/verify-email', end: true },
    title: 'E-mail Verifiëren | De CoBank',
    description: 'Verifieer je e-mailadres om je De CoBank account te activeren.',
    robots: 'noindex,follow',
    canonicalPath: '/verify-email',
    ogType: 'website'
  },
  {
    matcher: { path: '/topics', end: true },
    title: 'Onderwerpen | De CoBank',
    description: 'Overzicht van onderwerpen op De CoBank.',
    robots: 'noindex,follow',
    canonicalPath: '/topics',
    ogType: 'website'
  },
  {
    matcher: { path: '/topics/:topicId', end: true },
    title: 'Oefenen | De CoBank',
    description: 'Oefen vragen binnen een onderwerp op De CoBank.',
    robots: 'noindex,follow',
    ogType: 'website'
  },
  {
    matcher: { path: '/practice', end: true },
    title: 'Oefenen | De CoBank',
    description: 'Ga verder met oefenen op De CoBank.',
    robots: 'noindex,follow',
    canonicalPath: '/practice',
    ogType: 'website'
  },
  {
    matcher: { path: '/profile', end: true },
    title: 'Profiel | De CoBank',
    description: 'Beheer je profiel en instellingen op De CoBank.',
    robots: 'noindex,follow',
    canonicalPath: '/profile',
    ogType: 'website'
  },
  {
    matcher: { path: '/admin/*', end: false },
    title: 'Beheer | De CoBank',
    description: 'Beheeromgeving van De CoBank.',
    robots: 'noindex,nofollow',
    ogType: 'website'
  }
];

function normalizePath(pathname) {
  if (!pathname || pathname === '/') {
    return '/';
  }
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function buildCanonicalUrl(pathname) {
  return pathname === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${pathname}`;
}

function upsertMeta(attributeName, attributeValue, content) {
  let node = document.head.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attributeName, attributeValue);
    document.head.appendChild(node);
  }
  node.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let node = document.head.querySelector(`link[rel="${rel}"]`);
  if (!node) {
    node = document.createElement('link');
    node.setAttribute('rel', rel);
    document.head.appendChild(node);
  }
  node.setAttribute('href', href);
}

function resolveMeta(pathname) {
  const normalizedPath = normalizePath(pathname);
  const matchedMeta = ROUTE_META.find(({ matcher }) => matchPath(matcher, normalizedPath));

  if (!matchedMeta) {
    return {
      title: 'Pagina Niet Gevonden | De CoBank',
      description: DEFAULT_DESCRIPTION,
      robots: 'noindex,follow',
      canonicalUrl: buildCanonicalUrl(normalizedPath),
      ogType: 'website'
    };
  }

  const canonicalPath = matchedMeta.canonicalPath || normalizedPath;
  return {
    title: matchedMeta.title,
    description: matchedMeta.description,
    robots: matchedMeta.robots,
    canonicalUrl: buildCanonicalUrl(canonicalPath),
    ogType: matchedMeta.ogType || 'website'
  };
}

export default function SeoHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const { title, description, robots, canonicalUrl, ogType } = resolveMeta(pathname);

    document.title = title;

    upsertLink('canonical', canonicalUrl);
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', robots);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
  }, [pathname]);

  return null;
}

